import { useEffect, useRef, useState } from "react";
import type { AdminCleaner } from "@/mocks/admin";

type Props = {
  cleaner: AdminCleaner;
  onView: () => void;
  onStatusChange: (next: AdminCleaner["status"]) => void;
  onRemove: () => void;
};

const statusOptions: { id: AdminCleaner["status"]; label: string; icon: string }[] = [
  { id: "active", label: "Mark as Active", icon: "ri-checkbox-circle-line" },
  { id: "onboarding", label: "Mark as Onboarding", icon: "ri-user-add-line" },
  { id: "on_leave", label: "Mark as On leave", icon: "ri-zzz-line" },
];

export default function CleanerActionMenu({
  cleaner,
  onView,
  onStatusChange,
  onRemove,
}: Props) {
  const [open, setOpen] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowStatus(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const close = () => {
    setOpen(false);
    setShowStatus(false);
  };

  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0 transition-colors ${
          open
            ? "bg-background-200 text-foreground-900"
            : "text-foreground-500 hover:bg-background-200 hover:text-foreground-800"
        }`}
        aria-label="More actions"
      >
        <i className="ri-more-2-fill"></i>
      </button>
      {open && (
        <div className="absolute z-40 right-0 mt-1.5 w-56 bg-background-50 border border-background-200 rounded-xl py-1.5 shadow-sm">
          {!showStatus ? (
            <>
              <button
                onClick={() => {
                  onView();
                  close();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-background-100 cursor-pointer text-foreground-900"
              >
                <i className="ri-user-line text-sm w-4 text-foreground-600"></i>
                View profile
              </button>
              <button
                onClick={() => setShowStatus(true)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-background-100 cursor-pointer text-foreground-900"
              >
                <i className="ri-shield-user-line text-sm w-4 text-foreground-600"></i>
                <span className="flex-1 text-left">Change status</span>
                <i className="ri-arrow-right-s-line text-foreground-500"></i>
              </button>
              <a
                href={`mailto:cleaner-${cleaner.id}@purenest.co`}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-background-100 cursor-pointer text-foreground-900"
                onClick={close}
              >
                <i className="ri-message-3-line text-sm w-4 text-foreground-600"></i>
                Message {cleaner.name.split(" ")[0]}
              </a>
              <div className="my-1 border-t border-background-200"></div>
              <button
                onClick={() => {
                  onRemove();
                  close();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-accent-50 cursor-pointer text-accent-700"
              >
                <i className="ri-delete-bin-line text-sm w-4"></i>
                Remove from roster
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setShowStatus(false)}
                className="w-full flex items-center gap-1.5 px-3 py-2 text-[11px] text-foreground-600 hover:bg-background-100 cursor-pointer border-b border-background-200"
              >
                <i className="ri-arrow-left-s-line"></i>
                Back
              </button>
              {statusOptions.map((s) => (
                <button
                  key={s.id}
                  disabled={s.id === cleaner.status}
                  onClick={() => {
                    onStatusChange(s.id);
                    close();
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer ${
                    s.id === cleaner.status
                      ? "bg-background-100 text-foreground-500 cursor-not-allowed"
                      : "text-foreground-900 hover:bg-background-100"
                  }`}
                >
                  <i className={`${s.icon} text-sm w-4 text-foreground-600`}></i>
                  <span className="flex-1 text-left">{s.label}</span>
                  {s.id === cleaner.status && (
                    <i className="ri-check-line text-primary-600"></i>
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}